import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { stringify, parse } from 'query-string';
import debounce from 'lodash.debounce';

import { ParagraphSmallGray } from 'components/Typography';
import ContentSection from 'components/ContentSection';
import ConverterInput from 'components/ConverterInput';
import Option from 'components/FullCurrencyConverter/Option';
import DatePicker from 'components/FullCurrencyConverter/DatePicker';
import Alert from 'components/Alert';
import Select from 'components/FullCurrencyConverter/Select';
import CurrencyConverterDropdown from 'components/CurrencyConverterDropdown';
import ContentPlaceholder from 'components/ContentPlaceholder';
import CurrencyReverseButton from 'components/FullCurrencyConverter/CurrencyReverseButton';

import { withDetectDevice, withSeoData } from 'hocs';

import {
  fetchCurrencyRatesFromUrl,
  fetchBanks,
  fetchCurrencies,
  fetchCurrenciesInfo,
  fetchMonthlyExchangeRateDynamics,
  fetchYearlyExchangeRateDynamics,
} from 'actions';

import {
  getLink,
  DEFAULT_API_DATE_FORMAT,
  DEFAULT_CLIENT_DATE_FORMAT,
  getCurrencyTypeInURLFormat,
  getConverterLandingUrl,
  parseDate,
} from 'utils';

import {
  HeaderFilters,
  Title,
  MobileSelectWrapper,
  DesktopSelectWrapper,
  SelectLabel,
  ConverterWrapper,
  ConverterInputWrapper,
  DataPickerWrapper,
  AlertTitle,
  TopDescription,
  BottomDescription,
  Line,
  ReversedLink,
} from './styles';

class FullCurrencyConverter extends Component {
  static getDerivedStateFromProps(nextProps, prevState) {
    const {
      sourceValue,
      targetValue,
      sourceCurrency,
      targetCurrency,
      converterTypesOptions,
    } = nextProps;

    if (
      prevState.sourceValue === null ||
      prevState.targetValue === null ||
      // need for update targetValue, when something change
      prevState.converterTypesOptions !== nextProps.converterTypesOptions
    ) {
      return {
        sourceValue,
        targetValue,
        sourceCurrency,
        targetCurrency,
        converterTypesOptions,
      };
    }

    return null;
  }

  constructor(props) {
    super(props);

    this.state = {
      sourceValue: null,
      targetValue: null,
    };

    this.minDate = new Date(2005, 0, 1);
    this.maxDate = new Date();
    this.containerStyle = { position: 'static' };

    this.sourceSelectLabel = props.intl.formatMessage({
      id: 'tables.source.label',
    });
    this.datePickerLabel = props.intl.formatMessage({
      id: 'datePicker.label',
    });
    this.sourceLabel = props.intl.formatMessage({
      id: 'currencyConverter.input.source',
    });
    this.targetLabel = props.intl.formatMessage({
      id: 'currencyConverter.input.target',
    });
    this.noDataAlertTitle = props.intl.formatMessage({
      id: 'currencyConverter.noData.alert.title',
    });
    this.noDataAlertDescription = props.intl.formatMessage({
      id: 'currencyConverter.noData.alert.description',
    });
  }

  componentDidMount() {
    this.updateUrlConverterType();
  }

  componentDidUpdate(prevProps) {
    const {
      location,
      sourceCurrency,
      targetCurrency,
      sourceType,
      targetType,
    } = this.props;

    this.updateUrlConverterType();

    const prevDate = parse(prevProps.location.search).date;
    const currentDate = parse(location.search).date;

    if (
      prevDate !== currentDate ||
      prevProps.sourceCurrency !== sourceCurrency ||
      prevProps.targetCurrency !== targetCurrency ||
      prevProps.sourceType !== sourceType ||
      prevProps.targetType !== targetType
    ) {
      this.fetchData();
    }
  }

  updateUrlConverterType = () => {
    const {
      location,
      history,
      converterType,
      existedConverterType,
    } = this.props;

    if (
      existedConverterType !== null &&
      existedConverterType !== converterType
    ) {
      history.replace({
        search: stringify({
          ...parse(location.search),
          'converter-type': existedConverterType,
        }),
      });
    }
  };

  // eslint-disable-next-line react/sort-comp
  pushValueToUrl = debounce((sourceValue, targetValue, priority) => {
    const { history, location } = this.props;

    history.replace({
      search: stringify({
        ...parse(location.search),
        val1: sourceValue,
        val2: targetValue,
        priority,
      }),
    });
  }, 200);

  onChangeSourceInput = value => {
    const { sourceRate } = this.props;

    const targetValue = value === '' ? '' : value * sourceRate;

    this.setState(
      {
        sourceValue: value,
        targetValue,
      },
      () => {
        this.pushValueToUrl(value, targetValue, undefined);
      }
    );
  };

  onChangeTargetInput = value => {
    const { targetRate } = this.props;

    const sourceValue = value === '' ? '' : value * targetRate;

    this.setState(
      {
        sourceValue,
        targetValue: value,
      },
      () => {
        this.pushValueToUrl(sourceValue, value, 1);
      }
    );
  };

  onDateAccept = date => {
    const { history, location, timestamp } = this.props;

    const parsed = parseDate(date);

    if (!parsed) {
      return;
    }

    if (timestamp !== parsed.format(DEFAULT_CLIENT_DATE_FORMAT)) {
      const formattedDate = parsed.format(DEFAULT_API_DATE_FORMAT);

      history.replace({
        search: stringify({
          ...parse(location.search),
          date: formattedDate,
        }),
      });
    }
  };

  onRequestChange = value => {
    const { history, location } = this.props;

    // Якщо value починається з '?', це тільки query параметри
    if (value.startsWith('?')) {
      history.replace({
        pathname: location.pathname,
        search: value,
      });
    } else {
      // Якщо це повний URL, використовуємо як раніше
      const pathname = location.pathname.endsWith('/')
        ? `/${location.pathname.split('/')[1]}`
        : location.pathname;

      history.replace(`${pathname}${value}`);
    }
  };

  onSelectDropdownOption = (value, direction) => {
    const {
      location,
      match,
      history,
      sourceCurrency,
      targetCurrency,
      sourceType,
      targetType,
      sourceValue,
    } = this.props;
    const landing = !!match.params.val1;
    const [currency, type] = value.split('-');

    const searchParams = stringify({
      ...parse(location.search),
    });

    const getSearchString = (srcCurrency, srcType, tgtCurrency, tgtType) =>
      stringify({
        ...parse(location.search),
        from: `${srcCurrency}${getCurrencyTypeInURLFormat(srcType)}`,
        to: `${tgtCurrency}${getCurrencyTypeInURLFormat(tgtType)}`,
      });

    switch (direction) {
      case 'source':
        history.replace({
          pathname: landing
            ? getConverterLandingUrl({
                sourceValue,
                sourceCurrency: currency,
                sourceType: type,
                targetCurrency,
                targetType,
              })
            : location.pathname,
          search: landing
            ? searchParams
            : getSearchString(
                currency.toLowerCase(),
                type,
                targetCurrency,
                targetType
              ),
        });
        break;
      case 'target':
        history.replace({
          pathname: landing
            ? getConverterLandingUrl({
                sourceValue,
                sourceCurrency,
                sourceType,
                targetCurrency: currency,
                targetType: type,
              })
            : location.pathname,
          search: landing
            ? searchParams
            : getSearchString(
                sourceCurrency,
                sourceType,
                currency.toLowerCase(),
                type
              ),
        });
        break;
      default:
        break;
    }
  };

  fetchData = async () => {
    const { location, match, dispatch, intl } = this.props;

    const params = { location, match, locale: intl.locale };

    await Promise.all([
      dispatch(fetchCurrenciesInfo(params)),
      dispatch(fetchCurrencyRatesFromUrl(params)),
    ]);
    dispatch(fetchMonthlyExchangeRateDynamics(params));
    dispatch(fetchYearlyExchangeRateDynamics(params));
    dispatch(fetchBanks(params));
  };

  fetchCurrenciesByScroll = type => {
    const { location, match, dispatch, intl } = this.props;

    dispatch(fetchCurrencies({ location, match, locale: intl.locale, type }));
  };

  getConverterTypeOptions = () => {
    const { converterTypesOptions, location, match } = this.props;

    return converterTypesOptions.map(type => {
      const fullLink = getLink({
        location,
        match,
        name: 'converterType',
        type: 'search',
        value: type.id,
        pathnameSeparator: '-',
        toggle: {
          type: 'search',
          name: 'priority',
        },
      });

      // Розділяємо на pathname та search, беремо тільки search
      const [, search] = fullLink.split('?');
      const link = search ? `?${search}` : '';

      return {
        ...type,
        link,
        render: this.renderOption,
        mobileRender: this.renderMobileOption,
      };
    });
  };

  getReverseLink = () => {
    const {
      sourceValue,
      targetValue,
      sourceCurrency,
      targetCurrency,
      sourceType,
      targetType,
      location,
      match,
    } = this.props;
    const landing = !!match.params.val1;

    const search = parse(location.search);

    if (landing) {
      return {
        pathname: getConverterLandingUrl({
          sourceValue,
          sourceCurrency: targetCurrency,
          sourceType: targetType,
          targetCurrency: sourceCurrency,
          targetType: sourceType,
        }),
        search: stringify({
          ...parse(location.search),
        }),
      };
    }

    return {
      pathname: location.pathname,
      search: stringify({
        ...search,
        from: `${targetCurrency}${getCurrencyTypeInURLFormat(targetType)}`,
        to: `${sourceCurrency}${getCurrencyTypeInURLFormat(sourceType)}`,
        val1: targetValue,
        val2: sourceValue,
        priority: undefined,
      }),
    };
  };

  reverseCurrencies = () => {
    const { sourceCurrency, targetCurrency } = this.props;

    if (sourceCurrency === targetCurrency) {
      return;
    }

    const { history } = this.props;

    const reverseLink = this.getReverseLink();

    history.replace(reverseLink);
  };

  renderOption = props => {
    return <Option {...props}>{props.label}</Option>;
  };

  renderMobileOption = props => (
    <Option {...props} mobile>
      {props.label}
    </Option>
  );

  renderSelectLabel = label => <SelectLabel>{label}</SelectLabel>;

  renderSourceInput = () => {
    const { sourceValue } = this.state;

    return (
      <ConverterInput
        value={sourceValue}
        label={this.sourceLabel}
        onChange={this.onChangeSourceInput}
        maxLength="20"
      />
    );
  };

  renderTargetInput = () => {
    const { targetValue } = this.state;

    return (
      <ConverterInput
        value={targetValue}
        label={this.targetLabel}
        onChange={this.onChangeTargetInput}
        maxLength="20"
      />
    );
  };

  renderDatePicker = () => {
    const { intl, timestamp } = this.props;

    return (
      <DatePicker
        value={timestamp}
        label={this.datePickerLabel}
        onChange={this.onDateAccept}
        maxDate={this.maxDate}
        minDate={this.minDate}
        locale={intl.locale}
        format="dd.MM.yyyy"
        mask="__.__.____"
        renderInput={props => <DataPickerWrapper {...props} />}
      />
    );
  };

  renderReverseLink = () => (
    <ReversedLink to={this.getReverseLink()}>
      <CurrencyReverseButton />
    </ReversedLink>
  );

  render() {
    const { sourceValue, targetValue } = this.state;

    const {
      existedConverterType,
      sourceRate,
      targetRate,
      currenciesIsFetching,
      ratesIsFetching,
      timestamp,
      source,
      target,
      isMobile,
      match,
      staticContext,
      title,
      landingReverseLink,
      landingDescription1,
      landingDescription2,
      landingDescription3,
      currenciesItems,
    } = this.props;

    const landing = !!match.params.val1;

    const converterTypesOptions = this.getConverterTypeOptions();
    const reverseLink = this.getReverseLink();

    const noData = converterTypesOptions.length === 0;

    if (
      staticContext &&
      ((match.params.sourceCurrency && !source.code) ||
        (match.params.targetCurrency && !target.code))
    ) {
      staticContext.status = 404;

      return null;
    }

    return (
      <ContentSection as="section">
        <header>
          {landing && (
            <>
              <Title>
                {title || (
                  <ContentPlaceholder
                    style={{
                      height: '36px',
                      width: '100%',
                      maxWidth: '400px',
                    }}
                  />
                )}
              </Title>

              <TopDescription>
                {landingDescription1 || (
                  <ContentPlaceholder
                    style={{
                      height: '16px',
                      width: '100%',
                      maxWidth: '450px',
                    }}
                  />
                )}
              </TopDescription>
              <TopDescription>
                {landingDescription2 || (
                  <ContentPlaceholder
                    style={{
                      height: '16px',
                      width: '100%',
                      maxWidth: '450px',
                    }}
                  />
                )}
              </TopDescription>

              <Line />
            </>
          )}

          <Title data-type="landing" as={landing ? 'h3' : 'h1'}>
            {title}
          </Title>

          <HeaderFilters>
            {!noData && !ratesIsFetching && (
              <DesktopSelectWrapper>
                <SelectLabel>
                  {`${this.sourceSelectLabel}:`}
                  &nbsp;
                </SelectLabel>

                <Select
                  name="converterType"
                  onRequestChange={this.onRequestChange}
                  options={converterTypesOptions}
                  value={existedConverterType}
                  type="link"
                  isMobile={isMobile}
                  mobileSupport
                />
              </DesktopSelectWrapper>
            )}

            {ratesIsFetching && (
              <ContentPlaceholder
                style={{
                  height: '16px',
                  width: '227px',
                  marginRight: '16px',
                  marginBottom: '15px',
                }}
              />
            )}

            {!ratesIsFetching && (
              <DataPickerWrapper>
                <SelectLabel>
                  {`${this.datePickerLabel}:`}
                  &nbsp;
                </SelectLabel>
                <DatePicker
                  onAccept={this.onDateAccept}
                  value={timestamp}
                  minDate={this.minDate}
                  maxDate={this.maxDate}
                />
              </DataPickerWrapper>
            )}

            {ratesIsFetching && (
              <ContentPlaceholder
                style={{ height: '16px', width: '105px', marginBottom: '15px' }}
              />
            )}

            {landing && source.code !== target.code && (
              <ReversedLink to={reverseLink}>{landingReverseLink}</ReversedLink>
            )}

            <ConverterWrapper>
              <ConverterInputWrapper>
                <ConverterInput
                  label={this.sourceLabel}
                  value={sourceValue}
                  sourceCurrency={source.code}
                  targetCurrency={target.code}
                  currencyType={source.type}
                  rate={sourceRate}
                  onChange={this.onChangeSourceInput}
                  type="source"
                  noData={noData}
                  loading={ratesIsFetching}
                />

                <CurrencyConverterDropdown
                  currencyInfo={source}
                  items={currenciesItems}
                  loading={currenciesIsFetching}
                  onSelectOption={value => {
                    this.onSelectDropdownOption(value, 'source');
                  }}
                  onScrollEnd={this.fetchCurrenciesByScroll}
                  containerStyle={this.containerStyle}
                />
              </ConverterInputWrapper>
              <CurrencyReverseButton
                onReverse={this.reverseCurrencies}
                title={`${target.code} в ${source.code}`}
              />
              <ConverterInputWrapper>
                <ConverterInput
                  label={this.targetLabel}
                  value={targetValue}
                  sourceCurrency={target.code}
                  targetCurrency={source.code}
                  currencyType={target.type}
                  rate={targetRate}
                  onChange={this.onChangeTargetInput}
                  type="target"
                  noData={noData}
                  loading={ratesIsFetching}
                />

                <CurrencyConverterDropdown
                  currencyInfo={target}
                  items={currenciesItems}
                  loading={currenciesIsFetching}
                  onSelectOption={value => {
                    this.onSelectDropdownOption(value, 'target');
                  }}
                  onScrollEnd={this.fetchCurrenciesByScroll}
                  containerStyle={this.containerStyle}
                />
              </ConverterInputWrapper>
            </ConverterWrapper>
          </HeaderFilters>
        </header>

        {landing && (
          <BottomDescription>{landingDescription3}</BottomDescription>
        )}

        {!noData && (
          <MobileSelectWrapper>
            <Select
              name="converterType"
              label={this.sourceSelectLabel}
              onRequestChange={this.onRequestChange}
              options={converterTypesOptions}
              value={existedConverterType}
              isMobile={isMobile}
              mobileSupport
            />
          </MobileSelectWrapper>
        )}
        {noData && !ratesIsFetching && (
          <Alert type="danger" outline>
            <AlertTitle>{this.noDataAlertTitle}</AlertTitle>
            <ParagraphSmallGray>
              {this.noDataAlertDescription}
            </ParagraphSmallGray>
          </Alert>
        )}
      </ContentSection>
    );
  }
}

FullCurrencyConverter.propTypes = {
  converterType: PropTypes.string.isRequired,
  existedConverterType: PropTypes.string,
  sourceValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired,
  targetValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired,
  sourceRate: PropTypes.number.isRequired,
  targetRate: PropTypes.number.isRequired,
  sourceCurrency: PropTypes.string.isRequired,
  targetCurrency: PropTypes.string.isRequired,
  sourceType: PropTypes.string.isRequired,
  targetType: PropTypes.string.isRequired,
  timestamp: PropTypes.string.isRequired,
  converterTypesOptions: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      label: PropTypes.string,
      value: PropTypes.string,
    })
  ).isRequired,
  currenciesItems: PropTypes.shape({
    all: PropTypes.arrayOf(PropTypes.object),
    money: PropTypes.arrayOf(PropTypes.object),
    crypto: PropTypes.arrayOf(PropTypes.object),
  }).isRequired,
  currenciesIsFetching: PropTypes.bool.isRequired,
  ratesIsFetching: PropTypes.bool.isRequired,

  source: PropTypes.objectOf(PropTypes.string).isRequired,
  target: PropTypes.objectOf(PropTypes.string).isRequired,

  intl: PropTypes.shape({
    locale: PropTypes.string,
    formatMessage: PropTypes.func,
    formatNumber: PropTypes.func,
  }).isRequired,

  location: PropTypes.shape({
    pathname: PropTypes.string,
    search: PropTypes.string,
  }).isRequired,
  match: PropTypes.shape({
    params: PropTypes.objectOf(PropTypes.string).isRequired,
  }).isRequired,
  history: PropTypes.shape({
    push: PropTypes.func,
    replace: PropTypes.func,
  }).isRequired,
  staticContext: PropTypes.objectOf(PropTypes.any),

  dispatch: PropTypes.func.isRequired,

  isMobile: PropTypes.bool.isRequired,

  title: PropTypes.string.isRequired,
  landingReverseLink: PropTypes.string,
  landingDescription1: PropTypes.string,
  landingDescription2: PropTypes.string,
  landingDescription3: PropTypes.string,
};

FullCurrencyConverter.defaultProps = {
  landingReverseLink: '',
  landingDescription1: '',
  landingDescription2: '',
  landingDescription3: '',
  existedConverterType: null,
  staticContext: null,
};

export default withSeoData(withDetectDevice(FullCurrencyConverter));
